import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { resetMemberMfa } from '../../../../lib/mfa'
import { apiClient } from '../../../../api/client'
import {
    GUEST_ROLE_CODE,
    useAccessGrants,
    useRevokeAccessGrant,
    type AccessGrant,
} from '../../../../api/accessGrants'
import { InviteMemberDialog } from '../../../../components/InviteMemberDialog'
import {
    useMembers,
    useMemberMutations,
    useInvitations,
    useInvitationMutations,
    type Member,
    type Invitation,
} from '../../../../api/multiOrg'
import { useRoles, type RoleView } from '../../../../api/roles'
import { useCan, usePermissions, isSubsetOf } from '../../../../contexts/PermissionsContext'
import { useLocale } from '../../../../contexts/LocaleContext'
import { getLocalizedName } from '../../../../lib/localization'
import { Button } from '../../../../components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../../components/ui/dialog'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'
import { AppIcon } from '../../../../components/ui/icon'
import { mdiTrashCanOutline } from '@mdi/js'
import { Badge } from '../../../../components/ui/badge'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { EmptyState } from '../../../../components/ui/empty-state'
import { mdiAccountGroupOutline } from '@mdi/js'
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/tabs'
import { GUEST_ROLE, MEMBER_KINDS, isMemberKind, type MemberKind } from '../../../../lib/member-kinds'

interface MembersSearch {
    kind?: MemberKind
}

export const Route = createFileRoute('/$org/settings/members/')({
    // The tab is in the URL (spec 2026-10-06-04): Team, Residents, Service
    // providers. No kind is the Team tab.
    validateSearch: (search: Record<string, unknown>): MembersSearch =>
        isMemberKind(search.kind) && search.kind !== 'staff' ? { kind: search.kind } : {},
    component: MembersPage,
})

// Roles are org data, not a compile-time list: an organization can define its
// own on /$org/settings/roles, so the pickers below are fed by GET /roles and
// labelled from the API's multilingual `name` (spec 2026-09-04-02).

function MembersPage() {
    const { org } = Route.useParams()
    const { t } = useTranslation('multi-org')
    const { t: tRoles } = useTranslation('roles')
    const { locale } = useLocale()
    const navigate = useNavigate({ from: Route.fullPath })
    const kind: MemberKind = Route.useSearch().kind ?? 'staff'
    const { data: members, isLoading } = useMembers(org, { page: 1, limit: 50, kind })
    const { data: allInvitations } = useInvitations(org, 'pending')
    const invitations = useMemo(
        () => (allInvitations ?? []).filter((inv) => (inv.kind ?? 'staff') === kind),
        [allInvitations, kind]
    )
    const memberMutations = useMemberMutations(org)
    const invitationMutations = useInvitationMutations(org)

    const [inviteOpen, setInviteOpen] = useState(false)

    const [memberToRemove, setMemberToRemove] = useState<Member | null>(null)
    const [invitationToRevoke, setInvitationToRevoke] = useState<Invitation | null>(null)
    const [removeError, setRemoveError] = useState<string | null>(null)
    const [memberToResetMfa, setMemberToResetMfa] = useState<Member | null>(null)
    const queryClient = useQueryClient()
    // The factor kinds this organization accepts, so the members list shows
    // which of a member's factors actually count here (spec 2026-10-01-03).
    const { data: acceptedKinds } = useQuery({
        queryKey: ['session-policy', org],
        queryFn: async () => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/session-policy', {
                params: { path: { orgCode: org } },
            })
            if (error || !data) throw new Error('Failed to load the session policy')
            return data.data
        },
        select: (policy) => policy.effective.mfa_allowed_factors ?? undefined,
    })
    const resetMfaMutation = useMutation({
        mutationFn: (userUid: string) => resetMemberMfa(org, userUid),
        onSuccess: () => {
            setMemberToResetMfa(null)
            queryClient.invalidateQueries({ queryKey: ['members', org] })
        },
        onError: (err) => {
            setMemberToResetMfa(null)
            setRemoveError(parseErrorTitle(err))
        },
    })

    // Managing members is `members.write`, not a role-code rank comparison.
    // Fails closed while the membership summary loads (PermissionsContext).
    const isAdmin = useCan('members.write')
    const { permissions: myPermissions } = usePermissions()

    // The org's roles, for both the invite dialog and the per-member picker.
    // Only fetched when the caller may read them (members.read).
    const canReadRoles = useCan('members.read')
    const { data: rolesData } = useRoles(org, canReadRoles)
    const roles = useMemo<RoleView[]>(() => rolesData?.data ?? [], [rolesData])

    // A role the caller could not grant is offered but disabled: the backend
    // refuses it with 403 permission_escalation, and discovering that after
    // submitting is worse than seeing it greyed out.
    const assignableRoles = useMemo(
        () =>
            roles.map((r) => ({
                role: r,
                assignable: isSubsetOf(r.permissions, myPermissions),
            })),
        [roles, myPermissions]
    )

    const breadcrumbs = useMemo(
        () => [
            { title: t('members.breadcrumb_home'), path: `/${org}` },
            { title: t('members.breadcrumb_settings'), path: `/${org}/settings` },
            { title: t('members.breadcrumb_members') },
        ],
        [org, t]
    )
    useBreadcrumbs(breadcrumbs)

    // Labels come from the API's multilingual `name`; the raw code is the last
    // resort (a role the caller cannot read, or one removed since the row was
    // fetched).
    const roleLabel = (code?: string) => {
        if (!code) return '—'
        const role = roles.find((r) => r.code === code)
        if (role) {
            return (
                getLocalizedName(role.name as Record<string, string>, locale) || code
            )
        }
        return code
    }

    const handleRemove = async () => {
        if (!memberToRemove) return
        setRemoveError(null)
        try {
            await memberMutations.removeMember.mutateAsync(memberToRemove.user.uid)
            setMemberToRemove(null)
        } catch (err) {
            const parsed = parseErrorCode(err)
            if (parsed === 'last_owner_required') {
                setRemoveError(t('members.error_last_owner'))
            } else {
                setRemoveError(parseErrorTitle(err))
            }
        }
    }

    const handleRevoke = async () => {
        if (!invitationToRevoke) return
        try {
            await invitationMutations.revoke.mutateAsync(invitationToRevoke.uid)
        } catch (err) {
            console.error(err)
        } finally {
            setInvitationToRevoke(null)
        }
    }

    const handleResend = async (inv: Invitation) => {
        try {
            await invitationMutations.resend.mutateAsync(inv.uid)
        } catch (err) {
            console.error(err)
        }
    }

    return (
        <div className="container mx-auto py-8 max-w-4xl space-y-8" data-testid="members-page">
            <PageHeader
                icon={SECTION_ICONS.members}
                title={t('members.title')}
                description={t('members.subtitle', { org })}
                actions={
                <>
                {isAdmin && (
                    <>
                        <Button
                            onClick={() => setInviteOpen(true)}
                            data-testid="invite-member-btn"
                        >
                            {t('members.invite_button')}
                        </Button>
                        <InviteMemberDialog
                            org={org}
                            open={inviteOpen}
                            onOpenChange={setInviteOpen}
                            defaultKind={kind}
                        />
                    </>
                )}
                </>
                }
            />

            {removeError && (
                <div
                    className="bg-destructive/10 border border-destructive/20 rounded-md p-3"
                    data-testid="remove-error"
                >
                    <p className="text-sm text-destructive">{removeError}</p>
                </div>
            )}

            <Tabs
                value={kind}
                onValueChange={(value) =>
                    navigate({ search: value === 'staff' ? {} : { kind: value as MemberKind } })
                }
            >
                <TabsList data-testid="members-kind-tabs">
                    {MEMBER_KINDS.map((k) => (
                        <TabsTrigger key={k} value={k} data-testid={`members-tab-${k}`}>
                            {t(`members.tabs.${k}`)}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            <section data-testid="members-section">
                <h2 className="text-lg font-semibold mb-3">{t('members.section_active')}</h2>
                {isLoading ? (
                    <p className="text-muted-foreground">{t('members.loading')}</p>
                ) : (members?.data ?? []).length === 0 ? (
                    <EmptyState variant="inline" icon={mdiAccountGroupOutline} title={t(`members.empty_kind.${kind}`)} />
                ) : (
                    <div className="bg-card border border-border rounded-lg divide-y divide-border">
                        {(members?.data ?? []).map((m) => (
                            <MemberRow
                                key={m.user.uid}
                                org={org}
                                member={m}
                                isAdmin={isAdmin}
                                roleLabel={roleLabel}
                                assignableRoles={assignableRoles}
                                notAssignableLabel={tRoles('assign.not_assignable')}
                                onChangeRole={async (roleCode) => {
                                    try {
                                        await memberMutations.updateRole.mutateAsync({
                                            userUid: m.user.uid,
                                            roleCode,
                                        })
                                    } catch (err) {
                                        console.error(err)
                                    }
                                }}
                                onChangeKind={async (next) => {
                                    setRemoveError(null)
                                    try {
                                        // Residents and providers hold `guest`:
                                        // the role moves with the kind, in the
                                        // same request.
                                        await memberMutations.updateRole.mutateAsync({
                                            userUid: m.user.uid,
                                            kind: next,
                                            ...(next !== 'staff' && m.role?.code !== GUEST_ROLE
                                                ? { roleCode: GUEST_ROLE }
                                                : {}),
                                        })
                                    } catch (err) {
                                        setRemoveError(
                                            parseErrorCode(err) === 'role_not_allowed_for_kind'
                                                ? t('members.kind_change_error')
                                                : parseErrorTitle(err)
                                        )
                                    }
                                }}
                                kindLabel={(k) => t(`members.tabs.${k}`)}
                                onRemove={() => setMemberToRemove(m)}
                                onResetMfa={() => setMemberToResetMfa(m)}
                                acceptedKinds={acceptedKinds as string[] | undefined}
                                youLabel={t('members.you_badge')}
                                soleOwnerLabel={t('members.sole_owner_badge')}
                                removeLabel={t('members.remove_button')}
                                neverLabel={t('members.never')}
                            />
                        ))}
                    </div>
                )}
            </section>

            {isAdmin && (
                <section data-testid="pending-invitations-section">
                    <h2 className="text-lg font-semibold mb-3">
                        {t('members.section_pending')}
                    </h2>
                    {invitations.length === 0 ? (
                        <EmptyState variant="inline" title={t('members.no_pending')} />
                    ) : (
                        <div className="bg-card border border-border rounded-lg divide-y divide-border">
                            {invitations.map((inv) => (
                                <InvitationRow
                                    key={inv.uid}
                                    invitation={inv}
                                    roleLabel={roleLabel}
                                    expiresLabel={t('invitation_row.expires_on', {
                                        date: new Date(inv.expires_at).toLocaleDateString(),
                                    })}
                                    resendLabel={t('invitation_row.resend_button')}
                                    revokeLabel={t('invitation_row.revoke_button')}
                                    onRevoke={() => setInvitationToRevoke(inv)}
                                    onResend={() => handleResend(inv)}
                                />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Remove member confirmation */}
            <AlertDialog
                open={!!memberToRemove}
                onOpenChange={(open) => !open && setMemberToRemove(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('remove_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('remove_dialog.description', {
                                name:
                                    memberToRemove?.user.name ||
                                    memberToRemove?.user.email ||
                                    '',
                                org,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-remove-cancel">
                            {t('remove_dialog.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-remove-member"
                            onClick={handleRemove}
                        >
                            {t('remove_dialog.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reset a member's two-step verification (spec 2026-10-01-03) */}
            <AlertDialog
                open={!!memberToResetMfa}
                onOpenChange={(open) => !open && setMemberToResetMfa(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('members.mfa_reset_title', {
                                name: memberToResetMfa?.user.name || memberToResetMfa?.user.email || '',
                            })}
                        </AlertDialogTitle>
                        <AlertDialogDescription>{t('members.mfa_reset_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-reset-mfa-cancel">
                            {t('members.mfa_reset_cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-reset-mfa"
                            disabled={resetMfaMutation.isPending}
                            onClick={(e) => {
                                e.preventDefault()
                                if (memberToResetMfa) resetMfaMutation.mutate(memberToResetMfa.user.uid)
                            }}
                        >
                            {t('members.mfa_reset_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Revoke invitation confirmation */}
            <AlertDialog
                open={!!invitationToRevoke}
                onOpenChange={(open) => !open && setInvitationToRevoke(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('revoke_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('revoke_dialog.description', {
                                email: invitationToRevoke?.email || '',
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-revoke-cancel">
                            {t('revoke_dialog.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-revoke-invitation"
                            onClick={handleRevoke}
                        >
                            {t('revoke_dialog.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

function MemberRow({
    org,
    member,
    isAdmin,
    roleLabel,
    assignableRoles,
    notAssignableLabel,
    onChangeRole,
    onChangeKind,
    kindLabel,
    onRemove,
    onResetMfa,
    acceptedKinds,
    youLabel,
    soleOwnerLabel,
    removeLabel,
    neverLabel,
}: {
    org: string
    member: Member
    isAdmin: boolean
    roleLabel: (code?: string) => string
    assignableRoles: { role: RoleView; assignable: boolean }[]
    notAssignableLabel: string
    onChangeRole: (roleCode: string) => void
    onChangeKind: (kind: MemberKind) => void
    kindLabel: (kind: MemberKind) => string
    onRemove: () => void
    onResetMfa: () => void
    acceptedKinds?: string[]
    youLabel: string
    soleOwnerLabel: string
    removeLabel: string
    neverLabel: string
}) {
    // Current role lives both on the list row (for rendering the select) and
    // is preserved on the API side after updates; we fall back to the raw
    // code string if the role object is unavailable for some reason.
    const currentRole = member.role?.code ?? ''
    // Sole owner rows are visually disabled — removing the last owner is
    // blocked server-side anyway (409 last_owner_required), but we hide the
    // destructive button to match the spec wording.
    const memberKind: MemberKind = member.kind ?? 'staff'
    // A resident or a provider holds `guest`, everything else comes from
    // their grants: there is no membership role to pick (spec 2026-10-06-04).
    const canEditRole = isAdmin && !member.is_sole_owner && memberKind === 'staff'
    const canEditKind = isAdmin && !member.is_self && !member.is_sole_owner
    const canRemove = isAdmin && !member.is_sole_owner
    const { t: tMembers } = useTranslation('multi-org')
    const { t: tAuth } = useTranslation('auth')
    const mfaFactors = member.mfa_factors ?? []
    // The admin reset removes every factor (authenticator app, e-mail, SMS,
    // passkeys); one's own are managed from the profile.
    const canResetMfa = isAdmin && !member.is_self && mfaFactors.length > 0

    return (
        <div
            className="flex items-center justify-between p-4 gap-4"
            data-testid={`member-row-${member.user.uid}`}
        >
            <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">
                    {member.user.name || member.user.email || '—'}
                </div>
                <div className="text-sm text-muted-foreground truncate">
                    {member.user.email}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    {new Date(member.joined_at).toLocaleDateString()}
                    {member.last_login_at
                        ? ` · ${new Date(member.last_login_at).toLocaleDateString()}`
                        : ` · ${neverLabel}`}
                </div>
                <Link
                    to="/$org/settings/members/$userUid/sessions"
                    params={{ org, userUid: member.user.uid }}
                    className="block text-xs text-muted-foreground hover:underline hover:text-foreground"
                    data-testid={`member-sessions-${member.user.uid}`}
                >
                    {tMembers('members.active_sessions', { count: member.active_sessions ?? 0 })}
                </Link>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid={`member-mfa-${member.user.uid}`}>
                    <span>
                        {mfaFactors.length > 0
                            ? tMembers('members.mfa_factors', {
                                  factors: mfaFactors
                                      .map((k) => acceptedKinds && !acceptedKinds.includes(k)
                                          ? `${tAuth(`mfa.factor_kinds.${k}`)} (${tMembers('members.mfa_not_accepted')})`
                                          : tAuth(`mfa.factor_kinds.${k}`))
                                      .join(', '),
                              })
                            : tMembers('members.mfa_none')}
                    </span>
                    {canResetMfa && (
                        <button
                            type="button"
                            className="underline underline-offset-2 hover:text-foreground"
                            onClick={onResetMfa}
                            data-testid={`member-mfa-reset-${member.user.uid}`}
                        >
                            {tMembers('members.mfa_reset')}
                        </button>
                    )}
                </div>
                <MemberScope
                    org={org}
                    userUid={member.user.uid}
                    userLabel={member.user.name || member.user.email || ''}
                    roleCode={currentRole}
                />
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {member.is_self && (
                    <Badge variant="secondary" data-testid={`member-you-${member.user.uid}`}>
                        {youLabel}
                    </Badge>
                )}
                {member.is_sole_owner && (
                    <Badge variant="outline" data-testid={`member-sole-owner-${member.user.uid}`}>
                        {soleOwnerLabel}
                    </Badge>
                )}
                {canEditKind && (
                    <Select value={memberKind} onValueChange={(v) => onChangeKind(v as MemberKind)}>
                        <SelectTrigger
                            className="h-8 w-40"
                            data-testid={`kind-select-${member.user.uid}`}
                        >
                            <SelectValue>{kindLabel(memberKind)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {MEMBER_KINDS.map((k) => (
                                <SelectItem
                                    key={k}
                                    value={k}
                                    data-testid={`kind-option-${member.user.uid}-${k}`}
                                >
                                    {kindLabel(k)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                {canEditRole ? (
                    <Select
                        value={currentRole}
                        onValueChange={onChangeRole}
                    >
                        <SelectTrigger
                            className="h-8 w-36"
                            data-testid={`role-select-${member.user.uid}`}
                        >
                            <SelectValue>{roleLabel(currentRole)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {assignableRoles
                                .filter((entry) => !entry.role.scoped_only)
                                .map(({ role, assignable }) => (
                                <SelectItem
                                    key={role.uid}
                                    value={role.code}
                                    disabled={!assignable && role.code !== currentRole}
                                    title={
                                        assignable ? undefined : notAssignableLabel
                                    }
                                    data-testid={`role-option-${member.user.uid}-${role.code}`}
                                >
                                    {roleLabel(role.code)}
                                    {!assignable && role.code !== currentRole && (
                                        <span className="ml-2 text-xs text-muted-foreground">
                                            {notAssignableLabel}
                                        </span>
                                    )}
                                </SelectItem>
                            ))}
                            {/* The member's current role may be unreadable to
                                the caller (or freshly deleted); keep it as an
                                option so the Select has a matching value. */}
                            {currentRole &&
                                !assignableRoles.some((r) => r.role.code === currentRole) && (
                                    <SelectItem value={currentRole}>{currentRole}</SelectItem>
                                )}
                        </SelectContent>
                    </Select>
                ) : (
                    <Badge variant="outline" data-testid={`member-role-${member.user.uid}`}>
                        {roleLabel(currentRole)}
                    </Badge>
                )}
                {canRemove && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onRemove}
                        data-testid={`remove-member-${member.user.uid}`}
                    >
                        {removeLabel}
                    </Button>
                )}
            </div>
        </div>
    )
}

function InvitationRow({
    invitation,
    roleLabel,
    expiresLabel,
    resendLabel,
    revokeLabel,
    onRevoke,
    onResend,
}: {
    invitation: Invitation
    roleLabel: (code?: string) => string
    expiresLabel: string
    resendLabel: string
    revokeLabel: string
    onRevoke: () => void
    onResend: () => void
}) {
    return (
        <div
            className="flex items-center justify-between p-4 gap-4"
            data-testid={`invitation-row-${invitation.uid}`}
        >
            <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">{invitation.email}</div>
                <div className="text-sm text-muted-foreground">
                    {roleLabel(invitation.role?.code)} · {expiresLabel}
                </div>
            </div>
            <div className="flex gap-2 shrink-0">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onResend}
                    data-testid={`invitation-resend-${invitation.uid}`}
                >
                    {resendLabel}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRevoke}
                    data-testid={`invitation-revoke-${invitation.uid}`}
                >
                    {revokeLabel}
                </Button>
            </div>
        </div>
    )
}

// parseErrorTitle pulls the human-readable title out of an API error without
// leaking the raw JSON. The backend returns `{title, code}` per
// base.ErrorResponse — if we can parse it we show the title, otherwise we
// fall back to the raw string.
function parseErrorTitle(err: unknown): string {
    if (!err) return ''
    const text = err instanceof Error ? err.message : String(err)
    try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed.title === 'string') return parsed.title
    } catch {
        // not JSON — fall through
    }
    return text
}

function parseErrorCode(err: unknown): string | null {
    if (!err) return null
    const text = err instanceof Error ? err.message : String(err)
    try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed.code === 'string') return parsed.code
    } catch {
        // not JSON — fall through
    }
    return null
}

/**
 * The member's scope (spec 2026-09-04-03, frontend change 2): "Whole
 * organization" for an ordinary member, or the anchors of their asset-scoped
 * grants — "Building A + 2".
 *
 * It is one line under the member's name rather than a column of its own: the
 * common case says "Whole organization" and adding a column for it would push
 * the role select off a phone.
 */
function MemberScope({
    org,
    userUid,
    userLabel,
    roleCode,
}: {
    org: string
    userUid: string
    userLabel: string
    roleCode: string
}) {
    const { t, i18n } = useTranslation('access')
    const canRead = useCan('members.read')
    const canWrite = useCan('members.write')
    const { data } = useAccessGrants(org, { user_uid: userUid }, canRead)
    const revoke = useRevokeAccessGrant(org)
    const [open, setOpen] = useState(false)
    const [pendingRevoke, setPendingRevoke] = useState<AccessGrant | null>(null)

    if (!canRead) return null
    const grants = (data?.data ?? []) as AccessGrant[]
    if (grants.length === 0) {
        // A guest with no grant is not an organization-wide member with nothing
        // special: they are someone whose access has expired or been revoked,
        // and saying "Whole organization" there would be a lie.
        const label =
            roleCode === GUEST_ROLE_CODE ? t('scope.none') : t('scope.whole_organization')
        return (
            <div
                className="text-xs text-muted-foreground"
                data-testid={`member-scope-${userUid}`}
            >
                {label}
            </div>
        )
    }

    const first = grants[0]?.asset?.name ?? ''
    const label =
        grants.length === 1
            ? t('scope.one_place', { name: first })
            : t('scope.several_places', { name: first, count: grants.length - 1 })

    return (
        <>
            {/* The summary opens the drawer (spec 2026-09-04-03, frontend
                change 2): "Building A + 2" is enough to scan a list, and never
                enough to answer "which two, and until when?". Revoking from
                here is the same call the asset Access panel makes — a manager
                looking at a person should not have to go find the asset. */}
            <button
                type="button"
                className="text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
                onClick={() => setOpen(true)}
                data-testid={`member-scope-${userUid}`}
            >
                {label}
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid={`member-grants-drawer-${userUid}`}>
                    <DialogHeader>
                        <DialogTitle>{t('grants_drawer.title', { name: userLabel })}</DialogTitle>
                        <DialogDescription>{t('grants_drawer.description')}</DialogDescription>
                    </DialogHeader>
                    <ul className="divide-y">
                        {grants.map((grant) => (
                            <li
                                key={grant.uid}
                                className="flex items-center justify-between gap-3 py-2"
                                data-testid="member-grant-row"
                            >
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium">
                                        {grant.asset?.name ?? ''}
                                    </p>
                                    <p className="truncate text-xs text-muted-foreground">
                                        {grant.valid_until
                                            ? t('panel.until', {
                                                  date: new Date(
                                                      grant.valid_until
                                                  ).toLocaleDateString(i18n.language),
                                              })
                                            : t('grants_drawer.no_end_date')}
                                    </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <Badge variant="secondary">{grant.role?.code}</Badge>
                                    {canWrite && (
                                        <Button
                                            variant="destructive"
                                            size="icon"
                                            aria-label={t('panel.revoke')}
                                            title={t('panel.revoke')}
                                            data-testid="member-grant-revoke-btn"
                                            onClick={() => setPendingRevoke(grant)}
                                        >
                                            <AppIcon path={mdiTrashCanOutline} size="sm" />
                                        </Button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={Boolean(pendingRevoke)}
                onOpenChange={(isOpen) => !isOpen && setPendingRevoke(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('panel.revoke_confirm_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('panel.revoke_confirm_description', {
                                name: userLabel,
                                asset: pendingRevoke?.asset?.name ?? '',
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="member-grant-revoke-cancel">
                            {t('invite.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="member-grant-revoke-confirm"
                            onClick={() => {
                                if (pendingRevoke) {
                                    revoke.mutate({
                                        params: {
                                            path: { orgCode: org, grantUid: pendingRevoke.uid },
                                        },
                                    })
                                }
                                setPendingRevoke(null)
                            }}
                        >
                            {t('panel.revoke')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}
